// 004 (research R7; data-model §3): the parent's profile, children and addresses, in memory only.
// `refresh` loads all three in parallel; a failure keeps the previous data (offline display) and
// sets `error`. Mutations return the server result or throw AppError; collection-changing ones
// re-list (the server orders lists and picks the default address). Cleared on every sign-out.
import type { AppError } from "@/api/errors";
import { isAppError } from "@/api/errors";
import type { Api } from "@/api/types";

// Type only: the session provider imports this module, and the API client imports the session.
import type { ProfileClient } from "./profileApi";
import { appSelection } from "./selection";

export type Resource<T> = {
  status: "idle" | "loading" | "ready" | "error";
  data: T | null;
  error: AppError | null;
  loadedAt: number | null;
};

export type ParentData = {
  profile: Resource<Api["Profile"]>;
  students: Resource<Api["Student"][]>;
  addresses: Resource<Api["Address"][]>;
};

const idle = <T>(): Resource<T> => ({
  status: "idle",
  data: null,
  error: null,
  loadedAt: null,
});
const empty = (): ParentData => ({
  profile: idle(),
  students: idle(),
  addresses: idle(),
});

const unknownError = (e: unknown): AppError =>
  isAppError(e)
    ? e
    : {
        kind: "unknown",
        status: null,
        code: null,
        requestId: null,
        fieldErrors: [],
        retryAfterSeconds: null,
        allowedClients: null,
      };

export function createParentDataStore() {
  let state: ParentData = empty();
  let generation = 0;
  const listeners = new Set<() => void>();
  const set = (next: Partial<ParentData>) => {
    state = { ...state, ...next };
    listeners.forEach((l) => l());
  };

  async function load<K extends keyof ParentData>(
    key: K,
    fetcher: () => Promise<NonNullable<ParentData[K]["data"]>>,
  ): Promise<void> {
    const gen = generation;
    const prev = state[key];
    set({ [key]: { ...prev, status: "loading" } } as Partial<ParentData>);
    try {
      const data = await fetcher();
      if (gen !== generation) return;
      set({
        [key]: { status: "ready", data, error: null, loadedAt: Date.now() },
      } as Partial<ParentData>);
    } catch (e) {
      if (gen !== generation) return;
      set({
        [key]: { ...state[key], status: "error", error: unknownError(e) },
      } as Partial<ParentData>);
    }
  }

  const loadProfile = (c: ProfileClient) =>
    load("profile", () => c.getProfile());
  const loadStudents = (c: ProfileClient) =>
    load("students", async () => (await c.listStudents()).items);
  const loadAddresses = (c: ProfileClient) =>
    load("addresses", async () => (await c.listAddresses()).items);

  const ready = <T>(data: T): Resource<T> => ({
    status: "ready",
    data,
    error: null,
    loadedAt: Date.now(),
  });

  return {
    getSnapshot: (): ParentData => state,
    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    async refresh(c: ProfileClient): Promise<void> {
      await Promise.all([loadProfile(c), loadStudents(c), loadAddresses(c)]);
    },
    async refreshStudents(c: ProfileClient): Promise<void> {
      await loadStudents(c);
    },
    async updateProfile(c: ProfileClient, body: Api["ProfileUpdate"]) {
      const profile = await c.updateProfile(body);
      set({ profile: ready(profile) });
      return profile;
    },
    async addStudent(
      c: ProfileClient,
      body: Api["StudentCreate"],
      key: string,
    ) {
      const student = await c.addStudent(body, key);
      await loadStudents(c);
      return student;
    },
    async updateStudent(
      c: ProfileClient,
      id: string,
      body: Api["StudentUpdate"],
    ) {
      const student = await c.updateStudent(id, body);
      const list = state.students.data;
      if (list)
        set({ students: ready(list.map((s) => (s.id === id ? student : s))) });
      return student;
    },
    async removeStudent(c: ProfileClient, id: string) {
      await c.removeStudent(id);
      await loadStudents(c);
    },
    async addAddress(
      c: ProfileClient,
      body: Api["AddressCreate"],
      key: string,
    ) {
      const address = await c.addAddress(body, key);
      await Promise.all([loadAddresses(c), loadProfile(c)]);
      return address;
    },
    async updateAddress(
      c: ProfileClient,
      id: string,
      body: Api["AddressUpdate"],
    ) {
      const address = await c.updateAddress(id, body);
      const list = state.addresses.data;
      if (list)
        set({ addresses: ready(list.map((a) => (a.id === id ? address : a))) });
      return address;
    },
    async deleteAddress(c: ProfileClient, id: string) {
      await c.deleteAddress(id);
      await Promise.all([loadAddresses(c), loadProfile(c)]);
    },
    async setDefaultAddress(c: ProfileClient, id: string) {
      await c.setDefaultAddress(id);
      await Promise.all([loadAddresses(c), loadProfile(c)]);
    },
    clear(): void {
      generation += 1;
      state = empty();
      listeners.forEach((l) => l());
    },
  };
}

export type ParentDataStore = ReturnType<typeof createParentDataStore>;

/** The app's single parent data store (module level, memory only). */
export const parentData: ParentDataStore = createParentDataStore();

/** Personal-data cleaner: every sign-out cause (FR-007; constitution VI). */
export function clearParentData(): void {
  parentData.clear();
  appSelection.clear();
}
